import React, { useEffect, useState } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Box, adaptV4Theme } from '@mui/material';
import Home from './screens/Home';
import Services from './screens/Services';
import { englishFontFallbackMap, arabicFontFallbackMap } from '../shared/fonts';
import { createTheme, ThemeProvider, StyledEngineProvider, useMediaQuery } from '@mui/material';
import CourseDetail from '../shared/components/CourseDetail';
import ServiceDetails from './screens/ServiceDetails';
import TeamMemberProfile from '../shared/components/TeamMemberProfile';
import Contact from './screens/Contact';
import Footer from './components/Footer';
import ShortStyleFooter from '../shared/components/ShortStyleFooter';
import LongStyleFooter from '../shared/components/LongStyleFooter';
import Categories from './screens/Categories';
import CategoriesList from './screens/CategoriesList';
import WhatsappButton from '../shared/components/WhatsappButton';
import CustomPage from '../shared/components/CustomPage';
import TeamRegistration from './screens/TeamRegistration';
import Blog from './screens/Blog';
import { Helmet } from 'react-helmet';
import {
  CompanyDataProvider,
  useCompanyData,
} from '../shared/contexts/DataContext';
import moment from 'moment';
import Page404 from 'src/screens/Page404';
import Terms from './screens/Terms';
import Privacy from './screens/Privacy';
import RefundPolicy from './screens/RefundPolicy';
import Navbar from '../shared/components/Navbar';
import '../shared/styles.css';
import '../shared/fontsize.css';

export const theme = createTheme(adaptV4Theme({
  palette: {
    text: { primary: '#011615', secondary: '#C31B37' },
    primary: { main: '#C31B37' },
    secondary: { main: '#01161599' },
    background: { default: 'red' },
  },
  typography: {
    allVariants: {
      fontFamily: 'inherit',
    },
    h5: {
      fontWeight: 700,
    }
  },
  overrides: {
    MuiAppBar: { root: { boxShadow: 'none' } },

    MuiButton: {
      root: {
        // fontFamily: 'Nunito Sans',
        fontWeight: 600,
        textTransform: 'capitalize',
        boxShadow: 'none',
      },
      containedPrimary: {
        color: 'white',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
    },
    MuiCard: {
      root: {
        borderRadius: '4px',
        boxShadow: '0 0 5px rgb(209, 209, 209)',
      },
    },
  },
}));

const Template = ({ templateProps }) => {
  return (
    (<CompanyDataProvider>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <Helmet>
            <link rel="preconnect" href="https://fonts.gstatic.com" />
            <link
              href="https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,wght@0,200;0,300;0,400;0,600;0,700;0,800;0,900;1,200;1,300;1,400;1,600;1,700;1,800;1,900&display=swap"
              rel="stylesheet"
            />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link
              href="https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;600;700;900&display=swap"
              rel="stylesheet"
            ></link>
          </Helmet>
          <Main templateProps={templateProps} />
        </ThemeProvider>
      </StyledEngineProvider>
    </CompanyDataProvider>)
  );
};

const Main = ({ templateProps }) => {
  const { setData, data } = useCompanyData();
  const { pathname:path } = useLocation();
  const matches = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    setData(templateProps);
    moment.locale(templateProps.isRTL);
  }, [templateProps]);

  const { whatsappNumber } = templateProps.companyData || {};
  const { companyId, companyDomain, pageList, footer } = data || {};
  const { footerStyle } = footer || {};
  const isWeb = window.location.pathname.includes('/web');
  const pagePath = window?.location?.pathname?.split('/');
  const pageId = pagePath?.length ? pagePath[pagePath?.length - 1] : '';
  const showMenu = isWeb && pageId && pageList ? pageList?.[pageId]?.showMenu !== undefined ? pageList?.[pageId]?.showMenu : true : true;
  const englishFont = englishFontFallbackMap[templateProps?.englishFont] || "'Poppins', serif";
  const arabicFont = arabicFontFallbackMap[templateProps?.arabicFont] || '"Cairo", sans-serif';
  return (
    <Box
      fontFamily={templateProps.isRTL === 'ar' ? arabicFont : englishFont}
      minHeight="100vh"
      overflow="hidden"
      style={{position:'relative',
        background:
          templateProps.bodyBackgroundStyle === 'solidColor'
          ? templateProps.bodyBackgroundColor || '#f6f6f6'
          : templateProps.bodyBackgroundStyle === 'gradient'
          ? templateProps.bodyBackgroundColor || '#f6f6f6'
          : templateProps.bodyBackgroundStyle === 'image'
          ? `url(${templateProps.bodyBackgroundImage}) no-repeat center center fixed`
          : '#fff',
          ...(templateProps.bodyBackgroundStyle === 'image' ? {backgroundSize: matches ? 'cover' : 'contain'} : {})
      }}
      dir={templateProps.isRTL === 'ar' ? 'rtl' : 'ltr'}
    >
      {showMenu ?
      <Navbar
        defaultColor="#011615"
        activeColor="#C31B37"
        fontWeight={700}
        hasActiveBorderTop
        height="90px"
        matches={matches}
        fixPadding={!matches}
        // paddingZero={true}
        // transparencyColor='#fff'
        // bgcolor="#fff"
      />
      : null}
      <WhatsappButton {...{whatsappNumber}} />
      <Routes>
      <Route path={'/'} element={<Home />} />
        <Route path="/temp/services" element={<Services />} />
        <Route path="/temp/categories" element={<Categories />} />
        <Route path="/temp/services/:serviceId" element={<ServiceDetails />} />
        <Route path="/temp/categories/:categoryId" element={<CategoriesList />} />
        <Route path="/temp/web/:pageId" element={<CustomPage />} />
        <Route path="/temp/blog" element={<Blog />} />
        <Route path="/temp/contact" element={<Contact />} />
        <Route path="/temp/terms" element={<Terms />} />
        <Route path="/temp/privacy" element={<Privacy />} />
        <Route path="/temp/refund_policy" element={<RefundPolicy />} />
        <Route path="/temp/course/:courseId" element={<CourseDetail />} />
        <Route path="/temp/team/:teamId" element={<TeamMemberProfile />} />
        <Route path="/temp/team_registration" element={<TeamRegistration />} />

        {!companyDomain && (
          <>
          <Route path="/:id/temp/services" element={<Services />} />
          <Route path="/:id/temp/categories" element={<Categories />} />
          <Route path="/:id/temp/services/:serviceId" element={<ServiceDetails />} />
          <Route path="/:id/temp/categories/:categoryId" element={<CategoriesList />} />
          <Route path="/:id/temp/web/:pageId" element={<CustomPage />} />
          <Route path="/:id/temp/blog" element={<Blog />} />
          <Route path="/:id/temp/contact" element={<Contact />} />
          <Route path="/:id/temp/terms" element={<Terms />} />
          <Route path="/:id/temp/privacy" element={<Privacy />} />
          <Route path="/:id/temp/refund_policy" element={<RefundPolicy />} />
          <Route path="/:id/temp/course/:courseId" element={<CourseDetail />} />
          <Route path="/:id/temp/team/:teamId" element={<TeamMemberProfile />} />
          <Route path="/:id/temp/team_registration" element={<TeamRegistration />} />
        </>
        )}
         {/* Catch-all route for undefined paths */}
        <Route path="*" element={<Page404 />} />
      </Routes>
      {footerStyle === 'short' ? <ShortStyleFooter theme={theme} /> : <LongStyleFooter theme={theme} />}
      {/* <Footer /> */}
    </Box>
  );
};

export default Template;
